import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Grid, Paper, Typography, Chip, Select, MenuItem, LinearProgress, Box, Menu, Tooltip } from '@material-ui/core';
import { CircularProgress } from '@material-ui/core';
import server from 'server';
import { GridColumns, DataGrid, GridCellParams } from '@material-ui/data-grid';
import { Search } from 'components/Search';
import { getRowsPerPageOptions } from 'utils/pagination';
import { useStyles, BootstrapInput } from './styles';
import { RiskItem, Filters } from './types';
import PreviewIcon from '@material-ui/icons/RemoveRedEyeSharp';
import BuildIcon from '@material-ui/icons/Build';
import BlockIcon from '@material-ui/icons/Block';
import GetAppIcon from '@material-ui/icons/GetApp';
import { orange } from '@material-ui/core/colors';
import IconButton from '@material-ui/core/IconButton';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import axios from 'axios';
import { handleCompanyUrl } from 'utils/organizationUtils';
const base_url = process.env.REACT_APP_API_BASE_URL;

// const RECOMMENDATIONS: Record<string, string> = {
//     dormant: "Disable or remove accounts inactive for 90+ days.",
//     disabled: "Review disabled accounts and remove if no longer needed.",
//     mfa_gap: "Enable MFA for all privileged users immediately.",
//     orphaned: "Assign ownership or decommission orphaned accounts.",
// };

const normalizeRiskItems = (payload: any): RiskItem[] => {
  const source =
    payload?.data?.findings ??
    payload?.findings ??
    (Array.isArray(payload?.data) ? payload.data : null) ??
    payload?.data?.data ??
    [];
  return source.map((item: any) => ({
    id: item.id,
    userId: item.user?.id ?? item.user_id ?? 0,
    name: item.user?.name ?? item.user_email?.split('@')[0] ?? '-',
    email: item.user?.email ?? item.user_email ?? '-',
    riskType: item.risk_type ?? '-',
    severity: item.severity ?? '-',
    daysInactive: item.days_inactive ?? 0,
    mfaEnabled: item.mfa_enabled ?? item.risk_type !== 'mfa_gap',
    apps: item.connected_apps?.map((app: any) => app.app_name).join(', ') || '-',
    remediation_steps: item.remediation_steps?.map((s: any) => s).join(', ') || '-',
    createdAt: item.created_at ? new Date(item.created_at).toLocaleDateString() : '-',
    ignored: false,
  }));
};

const computeHygieneScore = (risks: RiskItem[], summary: any): number => {
  if (summary?.score != null) return summary.score;
  if (summary?.hygiene_score != null) return summary.hygiene_score;
  if (!risks.length) return 100;
  const critical = risks.filter((r) => r.severity === 'critical').length;
  const high = risks.filter((r) => r.severity === 'high').length;
  const medium = risks.filter((r) => r.severity === 'medium').length;
  const penalty = critical * 5 + high * 3 + medium * 1;
  return Math.max(0, Math.min(100, 100 - penalty));
};

const scoreLabel = (score: number) => (score >= 80 ? 'Low Risk' : score >= 50 ? 'Moderate Risk' : 'High Risk');

const scoreColor = (score: number) => (score >= 80 ? '#4caf50' : score >= 50 ? '#ff9800' : '#f44336');

const RiskScanner = observer(() => {
  const classes = useStyles();
  const [filter, setFilter] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [risks, setRisks] = useState<RiskItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<any>(null);
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [ignoredIds, setIgnoredIds] = useState<Set<number>>(new Set());

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleOpen = (row: any) => {
    setSelectedRow(row);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
  };

  const [filters, setFilters] = useState<Filters>({
    risk_type: '',
    severity: '',
    app_provider: '',
    date_range: '',
  });

  const hygieneScore = useMemo(() => computeHygieneScore(risks, summary), [risks, summary]);
  const hygieneMax = summary?.max ?? 100;

  // Counts for summary banner
  const mfaGaps = useMemo(
    () => summary?.mfa_gaps ?? risks.filter((r) => r.riskType === 'mfa_gap').length,
    [risks, summary]
  );
  const dormant = useMemo(
    () => summary?.dormant_accounts ?? risks.filter((r) => r.riskType === 'dormant').length,
    [risks, summary]
  );
  const orphaned = useMemo(
    () => summary?.orphaned_accounts ?? risks.filter((r) => r.riskType === 'orphaned').length,
    [risks, summary]
  );
  const criticalCount = useMemo(
    () => risks.filter((r) => r.severity === 'critical' || r.severity === 'high').length,
    [risks]
  );
  const overAll = useMemo(
    () => summary?.overall_risk ?? risks.filter((r) => r.riskType === 'overall_risk').length,
    [risks, summary]
  );
  const handleExportRowCSV = (row: RiskItem) => {
    const headers = ['Name', 'Email', 'Risk Type', 'Severity', 'Days Inactive', 'Apps', 'Remediation'];
    const values = [row.name, row.email, row.riskType, row.severity, row.daysInactive, row.apps, row.remediation_steps];
    const csv = [headers.join(','), values.map((v) => `"${v}"`).join(',')].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `risk-${row.email}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };
  const handleIgnore = async (id: number) => {
    try {
      await server.post(`/security/access-risks/${id}/dismiss`);

      // UI se remove kar do
      // setIgnoredIds(prev => new Set([...prev, id]));
      setIgnoredIds((prev) => new Set(Array.from(prev).concat(id)));

      // Ya agar latest data chahiye to
      // fetchRisks();
    } catch (err) {
      console.error('Failed to dismiss risk', err);
    }
  };
  const handleRemediate = async (id: number) => {
    try {
      const user = await server.get(`/ldap/user/${id}`);
      localStorage.setItem('ClickedUser', JSON.stringify(user.data.user));
      let url;
      if (user?.data?.message === 'User found') {
        url = handleCompanyUrl('/user/view');
        window.open(url, '_blank');
        return;
      }
      if (user.data.data.carlicense) {
        url = handleCompanyUrl('/admin/view');
        window.open(url, '_blank');
      } else {
        url = handleCompanyUrl('/user/view');
        window.open(url, '_blank');
      }
    } catch (err) {
      console.error('Failed to remediate', err);
    }
  };
  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('token');

      const { data } = await axios.get(`${base_url}security/reports/identity-risk`, {
        headers: {
          Authorization: token,
        },
      });

      const report = [
        ['Identity Risk Assessment'],
        [],
        ['Organization', data.organization],
        ['Date', data.date],
        [],
        ['Risk Summary'],
        [],
        [`🟢 ${data.risk_summary.active_users} active users`],
        [`🟠 ${data.risk_summary.dormant_accounts} dormant accounts`],
        [`🔴 ${data.risk_summary.users_without_mfa} users without MFA`],
        [`🟠 ${data.risk_summary.orphaned_accounts} orphaned accounts`],
        [`🟢 ${data.risk_summary.connected_apps} applications connected`],
        [],
        ['Overall Identity Hygiene Score'],
        [],
        [`${data.identity_hygiene_score.score} / ${data.identity_hygiene_score.max}`],
      ];

      const csv = report.map((row) => row.join(',')).join('\n');

      const blob = new Blob([csv], {
        type: 'text/csv;charset=utf-8;',
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = 'Identity-Risk-Assessment.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    }
  };
  const startScan = async () => {
    setScanning(true);
    setProgress(0);
    try {
      const interval = setInterval(() => {
        setProgress((prev) => (prev < 90 ? prev + 10 : prev));
      }, 200);
      const response = await server.post('/security/scan-access-risks', {});
      clearInterval(interval);
      setProgress(100);
      if (response.status === 200) {
        const scanData = response.data;
        setRisks(normalizeRiskItems(scanData));
        setSummary({
          score: scanData.identity_hygiene_score?.score,
          max: scanData.identity_hygiene_score?.max ?? 100,
          ...scanData.risk_summary,
          ...scanData?.data?.summary,
        });
      }
    } catch (error) {
      console.error('Scan failed:', error);
    } finally {
      setTimeout(() => {
        setScanning(false);
        setProgress(0);
      }, 1000);
    }
  };

  const fetchRisks = async () => {
    setLoading(true);
    try {
      const filteredParams = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
      );
      const params = new URLSearchParams(filteredParams).toString();
      const response = await server.get(`/security/access-risks${params ? `?${params}` : ''}`);
      if (response.status === 200) {
        const data = response.data;
        setRisks(normalizeRiskItems(data));
        setSummary({
          score: data.identity_hygiene_score?.score,
          max: data.identity_hygiene_score?.max ?? 100,
          ...data.risk_summary,
        });
      }
    } catch (error) {
      console.error('Failed to fetch risks:', error);
      setRisks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRisks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (key: keyof Filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const getSeverityClass = (severity: string) => {
    switch (severity) {
      case 'critical':
        return classes.criticalSeverity;
      case 'high':
        return classes.highSeverity;
      case 'medium':
        return classes.mediumSeverity;
      default:
        return classes.lowSeverity;
    }
  };

  const rows = useMemo(() => {
    const search = filter.trim().toLowerCase();
    const active = risks.filter((r) => !ignoredIds.has(r.id));
    if (!search) return active;
    return active.filter((r) =>
      [r.userId, r.name, r.email, r.riskType, r.severity, r.daysInactive, r.apps, r.createdAt]
        .join(' ')
        .toLowerCase()
        .includes(search)
    );
  }, [filter, risks, ignoredIds]);

  const rowsPerPageOptions = useMemo(() => getRowsPerPageOptions(rows), [rows]);

  const columns: GridColumns = [
    {
      field: 'name',
      headerName: 'FULL NAME',
      flex: 2.1,
    },
    {
      field: 'email',
      headerName: 'EMAIL',
      flex: 2.5,
    },
    {
      field: 'riskType',
      headerName: 'RISK TYPE',
      flex: 1.2,
    },
    {
      field: 'severity',
      headerName: 'SEVERITY',
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <Chip
          size='small'
          label={String(params.value).toUpperCase()}
          className={getSeverityClass(String(params.value).toLowerCase())}
        />
      ),
    },
    {
      field: 'daysInactive',
      headerName: 'DAYS INACTIVE',
      flex: 1,
    },
    {
      field: 'apps',
      headerName: 'CONNECTED APPS',
      flex: 1.5,
    },
    {
      field: 'actions',
      headerName: 'ACTIONS',
      flex: 1.6,
      disableColumnMenu: true,
      sortable: false,

      renderCell: (params: GridCellParams) => (
        <Box display='flex' alignItems='center'>
          <Tooltip title='View'>
            <IconButton size='small' onClick={() => handleOpen(params.row)}>
              <PreviewIcon style={{ color: orange[500], fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title='Remediate'>
            <IconButton size='small' onClick={() => handleRemediate((params.row as RiskItem).userId)}>
              <BuildIcon style={{ color: '#1976d2', fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title='Ignore'>
            <IconButton size='small' onClick={() => handleIgnore((params.row as RiskItem).id)}>
              <BlockIcon style={{ color: '#9e9e9e', fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title='Export Row'>
            <IconButton size='small' onClick={() => handleExportRowCSV(params.row as RiskItem)}>
              <GetAppIcon style={{ color: '#4caf50', fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      // renderCell: (params: GridCellParams) => (
      //     <Box display="flex" alignItems="center" style={{ gap: 2 }}>
      //         <Tooltip title="View">
      //             <IconButton size="small" onClick={() => handleOpen(params.row)}>
      //                 <PreviewIcon style={{ color: orange[500], fontSize: 18 }} />
      //             </IconButton>
      //         </Tooltip>
      //         {/* <Tooltip title="Remediate">
      //             <IconButton size="small" onClick={() => handleOpen(params.row)}>
      //                 <BuildIcon style={{ color: '#1976d2', fontSize: 18 }} />
      //             </IconButton>
      //         </Tooltip> */}
      //         <Tooltip title="Ignore">
      //             <IconButton size="small" onClick={() => setIgnoredIds(prev => new Set([...prev, params.row.id]))}>
      //                 <BlockIcon style={{ color: '#9e9e9e', fontSize: 18 }} />
      //             </IconButton>
      //         </Tooltip>
      //         <Tooltip title="Export Row">
      //             <IconButton size="small" onClick={() => handleExportRowCSV(params.row as RiskItem)}>
      //                 <GetAppIcon style={{ color: '#4caf50', fontSize: 18 }} />
      //             </IconButton>
      //         </Tooltip>
      //     </Box>
      // ),
    },
  ];

  return (
    <>
      {/* Filters Row */}
      <Grid container justify='space-between' alignItems='center' className={classes.box}>
        <div className={classes.filters}>
          <Select
            value={filters.risk_type}
            onChange={(e) => handleFilterChange('risk_type', e.target.value as string)}
            displayEmpty
            renderValue={(v) =>
              (v as string) ? (v as string).charAt(0).toUpperCase() + (v as string).slice(1) : 'Risk Type'
            }
            input={<BootstrapInput />}
          >
            <MenuItem value='dormant'>Dormant Users</MenuItem>
            <MenuItem value='disabled'>Disabled Accounts</MenuItem>
            <MenuItem value='mfa_gap'>MFA Gaps</MenuItem>
            <MenuItem value='orphaned'>Orphaned Accounts</MenuItem>
          </Select>
          <Select
            value={filters.severity}
            onChange={(e) => handleFilterChange('severity', e.target.value as string)}
            displayEmpty
            renderValue={(v) =>
              (v as string) ? (v as string).charAt(0).toUpperCase() + (v as string).slice(1) : 'Severity'
            }
            input={<BootstrapInput />}
          >
            <MenuItem value='critical'>Critical</MenuItem>
            <MenuItem value='high'>High</MenuItem>
            <MenuItem value='medium'>Medium</MenuItem>
            <MenuItem value='low'>Low</MenuItem>
          </Select>
          <Select
            value={filters.app_provider}
            onChange={(e) => handleFilterChange('app_provider', e.target.value as string)}
            displayEmpty
            renderValue={(v) =>
              (v as string) ? (v as string).charAt(0).toUpperCase() + (v as string).slice(1) : 'Provider'
            }
            input={<BootstrapInput />}
          >
            <MenuItem value='google'>Google Workspace</MenuItem>
            <MenuItem value='microsoft'>Microsoft 365</MenuItem>
            <MenuItem value='slack'>Slack</MenuItem>
            <MenuItem value='github'>GitHub</MenuItem>
          </Select>
          <Select
            value={filters.date_range}
            onChange={(e) => handleFilterChange('date_range', e.target.value as string)}
            displayEmpty
            renderValue={(v) => ((v as string) ? `Last ${v} Days` : 'Date Range')}
            input={<BootstrapInput />}
          >
            <MenuItem value='30'>Last 30 Days</MenuItem>
            <MenuItem value='60'>Last 60 Days</MenuItem>
            <MenuItem value='90'>Last 90 Days</MenuItem>
          </Select>
          <button onClick={fetchRisks} disabled={loading} className={classes.applyButton}>
            {loading ? 'Loading...' : 'Apply Filters & Get Report'}
          </button>
        </div>
      </Grid>

      {/* Search + Scan + Export */}
      <div className={classes.actions}>
        <Search handelSearch={setFilter} />
        <button onClick={startScan} disabled={scanning} className={classes.applyButton}>
          {scanning ? 'Scanning...' : 'Scan Environment'}
        </button>
        <div style={{ width: 'auto' }}>
          <Button onClick={handleMenuClick} style={{ color: '#4682B4', fontSize: '14px', padding: '8px 16px' }}>
            <GetAppIcon style={{ color: '#4682B4', fontSize: '20px', marginRight: '4px' }} />
            Export
          </Button>
          <Menu
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            style={{ top: '45px' }}
          >
            <MenuItem onClick={handleExportCSV}>Download Identity Risk Assessment (CSV)</MenuItem>
          </Menu>
        </div>
      </div>

      {/* Scan Progress */}
      {scanning && (
        <Paper className={classes.paper} elevation={0} style={{ padding: '20px', marginBottom: '16px' }}>
          <Typography variant='h6' style={{ marginBottom: '16px' }}>
            Scanning Environment...
          </Typography>
          <Box display='flex' alignItems='center'>
            <Box width='100%' mr={1}>
              <LinearProgress variant='determinate' value={progress} />
            </Box>
            <Box minWidth={35}>
              <Typography variant='body2' color='textSecondary'>{`${Math.round(progress)}%`}</Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Critical Findings Banner */}
      {risks.length > 0 && (
        <Paper
          elevation={0}
          style={{
            padding: '16px 20px',
            marginBottom: '16px',
            background: 'linear-gradient(135deg, #fff8f0 0%, #fff3e0 100%)',
            border: '1px solid #ffe0b2',
            borderRadius: 8,
          }}
        >
          <Typography variant='subtitle1' style={{ fontWeight: 700, marginBottom: 8, color: '#bf360c' }}>
            ⚠ Critical Findings
          </Typography>
          <Box style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {mfaGaps > 0 && (
              <Typography variant='body2'>
                🔴 <strong>{mfaGaps}</strong> MFA Gaps
              </Typography>
            )}
            {dormant > 0 && (
              <Typography variant='body2'>
                🟠 <strong>{dormant}</strong> Dormant Accounts
              </Typography>
            )}
            {orphaned > 0 && (
              <Typography variant='body2'>
                🟠 <strong>{orphaned}</strong> Orphaned Accounts
              </Typography>
            )}
            {criticalCount > 0 && (
              <Typography variant='body2'>
                🔴 <strong>{criticalCount}</strong> Critical/High Severity
              </Typography>
            )}
            <Typography
              variant='body2'
              style={{ marginLeft: 'auto', fontWeight: 600, color: scoreColor(hygieneScore) }}
            >
              Overall Risk: {overAll}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Identity Hygiene Score */}
      {risks.length > 0 && (
        <Paper
          elevation={0}
          style={{
            padding: '16px 20px',
            marginBottom: '16px',
            border: '1px solid #e0e0e0',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 24,
          }}
        >
          <div>
            <Typography
              variant='body2'
              color='textSecondary'
              style={{ fontWeight: 600, textTransform: 'uppercase', fontSize: 12 }}
            >
              Identity Hygiene Score
            </Typography>
            <Typography variant='h4' style={{ fontWeight: 700, color: scoreColor(hygieneScore), lineHeight: 1.2 }}>
              {hygieneScore} <span style={{ fontSize: 18, color: '#999' }}>/ {hygieneMax}</span>
            </Typography>
            <Chip
              size='small'
              label={scoreLabel(hygieneScore)}
              style={{
                backgroundColor: scoreColor(hygieneScore),
                color: '#fff',
                fontWeight: 600,
                marginTop: 4,
              }}
            />
          </div>
          <Box flex={1}>
            <LinearProgress variant='determinate' value={hygieneScore} style={{ height: 10, borderRadius: 5 }} />
          </Box>
        </Paper>
      )}

      {/* Scan Summary (after scan) */}
      {summary && (
        <Paper className={classes.paper} elevation={0} style={{ padding: '20px', marginBottom: '16px' }}>
          <Typography variant='h6' style={{ marginBottom: '16px' }}>
            Scan Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant='body2' color='textSecondary'>
                Total Risks
              </Typography>
              <Typography variant='h4'>{summary.total_risks}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant='body2' color='textSecondary'>
                High Severity
              </Typography>
              <Typography variant='h4' style={{ color: '#f44336' }}>
                {summary.high}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant='body2' color='textSecondary'>
                Medium Severity
              </Typography>
              <Typography variant='h4' style={{ color: '#ff9800' }}>
                {summary.medium}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant='body2' color='textSecondary'>
                Low Severity
              </Typography>
              <Typography variant='h4'>{summary.low}</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Data Table */}
      <Paper className={classes.paper} elevation={0}>
        {loading || scanning ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : rows.length === 0 ? (
          <Typography className={classes.noResults}>No risk records found</Typography>
        ) : (
          <Paper className={classes.dataGrid} elevation={0}>
            <DataGrid
              className={classes.noBorder}
              autoHeight
              rows={rows}
              columns={columns}
              pageSize={pageSize}
              rowsPerPageOptions={rowsPerPageOptions}
              pagination
              onPageSizeChange={(params) => setPageSize(params.pageSize)}
              disableSelectionOnClick
              rowHeight={56}
            />
          </Paper>
        )}
      </Paper>

      {/* View / Remediate Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth>
        <DialogTitle>User Risk Details</DialogTitle>
        <DialogContent dividers>
          <Typography>
            <strong>Name:</strong> {selectedRow?.name}
          </Typography>

          <Typography>
            <strong>Email:</strong> {selectedRow?.email}
          </Typography>

          <Typography>
            <strong>Risk Type:</strong> {selectedRow?.riskType}
          </Typography>

          <Typography>
            <strong>Severity:</strong> {selectedRow?.severity}
          </Typography>

          <Typography>
            <strong>Days Inactive:</strong> {selectedRow?.daysInactive}
          </Typography>

          <Typography>
            <strong>Connected Apps:</strong> {selectedRow?.apps}
          </Typography>
          <Typography>
            <strong>Remediation Steps:</strong> {selectedRow?.remediation_steps}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} variant='contained'>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default RiskScanner;
